import React, { useState, useEffect } from 'react';
import { isWhatsAppVerificationEnabled } from '../utils/featureFlags';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function UserSignup({ onSignupSuccess, onSwitchToLogin, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    occupation: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Feature flag for WhatsApp verification
  const [whatsappVerificationEnabled, setWhatsappVerificationEnabled] = useState(false);

  // WhatsApp OTP verification states
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Load feature flags on component mount
  useEffect(() => {
    const loadFeatureFlags = async () => {
      const enabled = await isWhatsAppVerificationEnabled();
      setWhatsappVerificationEnabled(enabled);
      // If WhatsApp verification is disabled, mark as verified
      if (!enabled) {
        setOtpVerified(true);
      }
    };
    loadFeatureFlags();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    // Reset OTP verification if phone number changes
    if (e.target.name === 'phone') {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpCode('');
      setOtpError('');
      setSuccessMessage('');
    }
  };

  const handleSendOTP = async () => {
    setOtpError('');
    setSuccessMessage('');

    // Validate phone number
    if (!formData.phone) {
      setOtpError('Please enter your WhatsApp number');
      return;
    }

    // Validate phone number format (must include country code)
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(formData.phone.replace(/[\s-]/g, ''))) {
      setOtpError('Please enter a valid phone number with country code (e.g., +91XXXXXXXXXX)');
      return;
    }

    setOtpLoading(true);

    try {
      const response = await fetch(`${API_URL}/user/send-whatsapp-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formData.phone.replace(/[\s-]/g, '')
        }),
      });

      const result = await response.json();

      if (result.success) {
        setOtpSent(true);
        setSuccessMessage('OTP sent to your WhatsApp! Please check your messages.');
      } else {
        setOtpError(result.message || 'Failed to send OTP');
      }
    } catch (error) {
      setOtpError('An error occurred. Please try again.');
      console.error('Send OTP error:', error);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setOtpError('');
    setSuccessMessage('');

    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit OTP code');
      return;
    }

    setOtpLoading(true);

    try {
      const response = await fetch(`${API_URL}/user/verify-whatsapp-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formData.phone.replace(/[\s-]/g, ''),
          otpCode: otpCode
        }),
      });

      const result = await response.json();

      if (result.success) {
        setOtpVerified(true);
        setSuccessMessage('WhatsApp number verified successfully! ✓');
      } else {
        setOtpError(result.message || 'Invalid OTP code');
      }
    } catch (error) {
      setOtpError('An error occurred. Please try again.');
      console.error('Verify OTP error:', error);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate WhatsApp verification (only if feature is enabled)
    if (whatsappVerificationEnabled && !otpVerified) {
      setError('Please verify your WhatsApp number before signing up');
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/user/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          occupation: formData.occupation,
          password: formData.password
        }),
      });

      const result = await response.json();

      if (result.success) {
        onSignupSuccess();
      } else {
        setError(result.message || 'Signup failed');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        maxWidth: '500px',
        width: '100%'
      }}>
        <h2 style={{ textAlign: 'center', color: '#2c3e50', marginBottom: '30px' }}>
          Create Your Account
        </h2>

        {error && (
          <div style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '5px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                marginBottom: '15px'
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                marginBottom: '15px'
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">WhatsApp Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+91XXXXXXXXXX"
              required
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                marginBottom: '10px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 10px 0' }}>
              Include country code (e.g., +91 for India)
            </p>

            {/* Success message */}
            {successMessage && (
              <div style={{
                backgroundColor: '#d4edda',
                color: '#155724',
                padding: '10px',
                borderRadius: '5px',
                marginBottom: '10px',
                fontSize: '13px'
              }}>
                {successMessage}
              </div>
            )}

            {/* OTP error message */}
            {otpError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '10px',
                borderRadius: '5px',
                marginBottom: '10px',
                fontSize: '13px'
              }}>
                {otpError}
              </div>
            )}

            {/* Send OTP Button */}
            {whatsappVerificationEnabled && !otpSent && !otpVerified && (
              <button
                type="button"
                onClick={handleSendOTP}
                disabled={otpLoading || !formData.phone}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: otpLoading ? '#95a5a6' : '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: otpLoading || !formData.phone ? 'not-allowed' : 'pointer',
                  marginBottom: '15px'
                }}
              >
                {otpLoading ? 'Sending OTP...' : 'Send OTP to WhatsApp'}
              </button>
            )}

            {/* OTP Input and Verify Button */}
            {whatsappVerificationEnabled && otpSent && !otpVerified && (
              <div style={{ marginBottom: '15px' }}>
                <input
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength="6"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    marginBottom: '10px',
                    textAlign: 'center',
                    letterSpacing: '5px',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}
                />
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={handleVerifyOTP}
                    disabled={otpLoading || otpCode.length !== 6}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: otpLoading || otpCode.length !== 6 ? '#95a5a6' : '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: otpLoading || otpCode.length !== 6 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {otpLoading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                  <button
                    type="button"
                    onClick={handleSendOTP}
                    disabled={otpLoading}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: '#f39c12',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: otpLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Resend OTP
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="occupation">Occupation</label>
            <input
              type="text"
              id="occupation"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
              placeholder="e.g., Student, Researcher, Professor"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                marginBottom: '15px'
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                marginBottom: '15px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#666', margin: '-10px 0 15px 0' }}>
              Minimum 6 characters
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                marginBottom: '20px'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="submit-btn"
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: loading ? '#95a5a6' : '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: '15px'
            }}
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>

          <div style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
            Already have an account?{' '}
            <span
              onClick={onSwitchToLogin}
              style={{
                color: '#3498db',
                cursor: 'pointer',
                fontWeight: 'bold',
                textDecoration: 'underline'
              }}
            >
              Login
            </span>
          </div>

          {onCancel && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                type="button"
                onClick={onCancel}
                style={{
                  backgroundColor: 'transparent',
                  color: '#7f8c8d',
                  border: '1px solid #bdc3c7',
                  padding: '10px 24px',
                  borderRadius: '5px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#ecf0f1';
                  e.target.style.borderColor = '#95a5a6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = '#bdc3c7';
                }}
              >
                ← Back to Home
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default UserSignup;
